const JPEG_METADATA_MARKERS = new Set([0xe0, 0xe1, 0xe2, 0xed]);
const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);
const PNG_METADATA_TYPES = new Set([
  "tEXt",
  "zTXt",
  "iTXt",
  "iCCP",
  "pHYs",
  "eXIf",
]);
const WEBP_METADATA_TYPES = new Set(["ICCP", "EXIF", "XMP "]);

function concatBytes(parts: Uint8Array[]): Uint8Array {
  const size = parts.reduce((total, part) => total + part.length, 0);
  const result = new Uint8Array(size);
  let offset = 0;
  parts.forEach((part) => {
    result.set(part, offset);
    offset += part.length;
  });
  return result;
}

function readAscii(bytes: Uint8Array, offset: number, length: number): string {
  return String.fromCharCode(...bytes.slice(offset, offset + length));
}

function writeAscii(bytes: Uint8Array, offset: number, value: string): void {
  for (let index = 0; index < value.length; index += 1) {
    bytes[offset + index] = value.charCodeAt(index);
  }
}

function normalizeExifOrientation(segment: Uint8Array): Uint8Array {
  const copy = new Uint8Array(segment);
  if (copy.length < 18 || readAscii(copy, 4, 6) !== "Exif\u0000\u0000") {
    return copy;
  }
  const tiff = 10;
  const littleEndian = readAscii(copy, tiff, 2) === "II";
  const view = new DataView(copy.buffer, copy.byteOffset, copy.byteLength);
  const ifdOffset = view.getUint32(tiff + 4, littleEndian);
  const directory = tiff + ifdOffset;
  if (directory + 2 > copy.length) return copy;
  const entries = view.getUint16(directory, littleEndian);
  for (let index = 0; index < entries; index += 1) {
    const entry = directory + 2 + index * 12;
    if (entry + 12 > copy.length) break;
    if (view.getUint16(entry, littleEndian) === 0x0112) {
      view.setUint16(entry + 8, 1, littleEndian);
      break;
    }
  }
  return copy;
}

function jpegSegments(bytes: Uint8Array): {
  metadata: Uint8Array[];
  body: Uint8Array;
} {
  if (bytes[0] !== 0xff || bytes[1] !== 0xd8) {
    return { metadata: [], body: bytes };
  }
  const metadata: Uint8Array[] = [];
  const bodyParts: Uint8Array[] = [bytes.slice(0, 2)];
  let offset = 2;
  while (offset + 4 <= bytes.length && bytes[offset] === 0xff) {
    const marker = bytes[offset + 1];
    if (marker === 0xda || marker === 0xd9) {
      bodyParts.push(bytes.slice(offset));
      return { metadata, body: concatBytes(bodyParts) };
    }
    const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
    if (length < 2 || offset + length + 2 > bytes.length) break;
    const segment = bytes.slice(offset, offset + length + 2);
    if (JPEG_METADATA_MARKERS.has(marker)) {
      metadata.push(marker === 0xe1 ? normalizeExifOrientation(segment) : segment);
    } else {
      bodyParts.push(segment);
    }
    offset += length + 2;
  }
  bodyParts.push(bytes.slice(offset));
  return { metadata, body: concatBytes(bodyParts) };
}

function preserveJpegMetadata(
  original: Uint8Array,
  generated: Uint8Array,
): Uint8Array {
  const originalParts = jpegSegments(original);
  const generatedParts = jpegSegments(generated);
  if (originalParts.metadata.length === 0) return generated;
  return concatBytes([
    generatedParts.body.slice(0, 2),
    ...originalParts.metadata,
    generatedParts.body.slice(2),
  ]);
}

interface PngChunk {
  type: string;
  bytes: Uint8Array;
}

const MAX_CHUNK_SIZE = 64 * 1024 * 1024;

function pngChunks(bytes: Uint8Array): PngChunk[] {
  if (
    bytes.length < PNG_SIGNATURE.length ||
    !PNG_SIGNATURE.every((value, index) => bytes[index] === value)
  ) {
    return [];
  }
  const chunks: PngChunk[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = view.getUint32(offset);
    if (length > MAX_CHUNK_SIZE) break;
    const end = offset + 12 + length;
    if (end > bytes.length) break;
    const type = readAscii(bytes, offset + 4, 4);
    chunks.push({ type, bytes: bytes.slice(offset, end) });
    offset = end;
    if (type === "IEND") break;
  }
  return chunks;
}

function preservePngMetadata(
  original: Uint8Array,
  generated: Uint8Array,
): Uint8Array {
  const metadata = pngChunks(original)
    .filter((chunk) => PNG_METADATA_TYPES.has(chunk.type))
    .map((chunk) => chunk.bytes);
  if (metadata.length === 0) return generated;
  const generatedChunks = pngChunks(generated);
  if (generatedChunks.length === 0) return generated;
  const output: Uint8Array[] = [PNG_SIGNATURE];
  let inserted = false;
  generatedChunks.forEach((chunk) => {
    if (!inserted && chunk.type === "IDAT") {
      output.push(...metadata);
      inserted = true;
    }
    output.push(chunk.bytes);
  });
  return concatBytes(output);
}

interface WebPChunk {
  type: string;
  payload: Uint8Array;
}

function webpChunks(bytes: Uint8Array): WebPChunk[] {
  if (
    bytes.length < 12 ||
    readAscii(bytes, 0, 4) !== "RIFF" ||
    readAscii(bytes, 8, 4) !== "WEBP"
  ) {
    return [];
  }
  const chunks: WebPChunk[] = [];
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let offset = 12;
  while (offset + 8 <= bytes.length) {
    const type = readAscii(bytes, offset, 4);
    const size = view.getUint32(offset + 4, true);
    if (offset + 8 + size > bytes.length) break;
    chunks.push({
      type,
      payload: bytes.slice(offset + 8, offset + 8 + size),
    });
    offset += 8 + size + (size % 2);
  }
  return chunks;
}

function encodeWebPChunks(chunks: WebPChunk[]): Uint8Array {
  const parts: Uint8Array[] = [];
  chunks.forEach((chunk) => {
    const bytes = new Uint8Array(8 + chunk.payload.length + (chunk.payload.length % 2));
    writeAscii(bytes, 0, chunk.type);
    new DataView(bytes.buffer).setUint32(4, chunk.payload.length, true);
    bytes.set(chunk.payload, 8);
    parts.push(bytes);
  });
  const payload = concatBytes(parts);
  const output = new Uint8Array(12 + payload.length);
  writeAscii(output, 0, "RIFF");
  new DataView(output.buffer).setUint32(4, output.length - 8, true);
  writeAscii(output, 8, "WEBP");
  output.set(payload, 12);
  return output;
}

function preserveWebPMetadata(
  original: Uint8Array,
  generated: Uint8Array,
): Uint8Array {
  const originalChunks = webpChunks(original);
  const metadata = originalChunks.filter((chunk) =>
    WEBP_METADATA_TYPES.has(chunk.type),
  );
  if (metadata.length === 0) return generated;
  const generatedChunks = webpChunks(generated).filter(
    (chunk) => !WEBP_METADATA_TYPES.has(chunk.type),
  );
  if (generatedChunks.length === 0) return generated;

  const originalExtended = originalChunks.find((chunk) => chunk.type === "VP8X");
  const generatedExtended = generatedChunks.find((chunk) => chunk.type === "VP8X");
  const extended = generatedExtended ?? originalExtended;
  if (extended?.payload.length) {
    const flags = new Uint8Array(extended.payload);
    if (metadata.some((chunk) => chunk.type === "ICCP")) flags[0] |= 0x20;
    if (metadata.some((chunk) => chunk.type === "EXIF")) flags[0] |= 0x08;
    if (metadata.some((chunk) => chunk.type === "XMP ")) flags[0] |= 0x04;
    extended.payload = flags;
  }

  const imageEnd = generatedChunks.findLastIndex((chunk) =>
    ["VP8 ", "VP8L", "ANMF"].includes(chunk.type),
  );
  generatedChunks.splice(imageEnd + 1, 0, ...metadata);
  return encodeWebPChunks(generatedChunks);
}

export async function preserveMetadata(
  originalBytes: ArrayBuffer,
  generatedBlob: Blob,
  mime: string,
): Promise<Blob> {
  const original = new Uint8Array(originalBytes);
  const generated = new Uint8Array(await generatedBlob.arrayBuffer());
  let result: Uint8Array<ArrayBufferLike> = generated;
  if (mime === "image/jpeg") {
    result = preserveJpegMetadata(original, generated);
  } else if (mime === "image/png") {
    result = preservePngMetadata(original, generated);
  } else if (mime === "image/webp") {
    result = preserveWebPMetadata(original, generated);
  }
  return new Blob([result.slice().buffer as ArrayBuffer], { type: mime });
}
